/**
 * Progressive passkey enrollment (#1902, plan §3 — the eBay pattern).
 *
 * The prompt appears after a successful OTP login and is an offer, never a
 * gate: OTP stays the visible path, so someone who declines, or whose device
 * has no authenticator, is never worse off.
 *
 * "Dismissal must be remembered, per account" — per account, not per device,
 * because a shared device would otherwise leak one person's decision onto the
 * next. Keyed by account id (and tenant, like the session token), so logging
 * in as someone else on the same phone asks them once.
 */

const dismissKey = (tenant: string, accountId: string): string =>
  `passkey_prompt_dismissed:${tenant}:${accountId}`;

export const isPasskeyPromptDismissed = (
  tenant: string,
  accountId: string,
): boolean => {
  try {
    return localStorage.getItem(dismissKey(tenant, accountId)) === "1";
  } catch {
    // Private mode / storage disabled: asking again is the safe failure.
    return false;
  }
};

export const dismissPasskeyPrompt = (
  tenant: string,
  accountId: string
): void => {
  try {
    localStorage.setItem(dismissKey(tenant, accountId), "1");
  } catch {
    // Not being able to remember the dismissal is not worth an error state.
  }
};

/**
 * Whether this browser can do WebAuthn at all. Checked before the prompt is
 * rendered so we never offer something that cannot work — and before the
 * login screen shows a passkey button.
 */
export const isPasskeySupported = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.PublicKeyCredential !== "undefined";
